/**
 * T27.2.7.9.4 — the shared in-place deal overlay.
 *
 * `DealDetailSheet` (the bottom-sheet deal detail, with its cross-store
 * comparison) is the ONE deal pop-up. It used to live inside `DealsPage`; it now
 * lives here so the shopping-list rows can open the exact same sheet IN PLACE —
 * an overlay over the current list — instead of navigating to `/deals?deal=…`.
 *
 * `DealOverlay` is the list's entry point: given a `flyerdeal_id`, it loads the
 * SAME full deals card set `DealsPage` loads (every selected store's offers +
 * the grouped flyer deals), finds the card for that deal, and renders
 * `DealDetailSheet` with the full `allCards`. Loading the full set is required —
 * `DealDetailSheet`'s "Also at your stores" comparison reads `allCards`, so a
 * single-deal fetch would silently drop the comparison block (parent § Risks).
 *
 * No fork: `DealsPage` imports `DealDetailSheet` + the card model/mappers/styles
 * from here and renders the same component. Tokens-only (CP5).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import {
  useSelectedStores,
  useStores,
  useRetailerOffers,
  useFlyerDeals,
  useActiveShoppingList,
  useHouseholdProducts,
  useAddToActiveList,
  useDeleteShoppingListItem,
} from '@shopista/shared/api/hooks';
import type {
  AddShoppingListItemRequest,
  FlyerDealsRow,
  FlyerDealsStoreGroup,
  FlyerDealLine,
  RetailerOfferWithDetails,
  ShoppingListItemWithDetails,
  DealsSearchRow,
  PriceBasis,
} from '@shopista/shared/types';
import { Modal } from '../../components';
import { deriveBadges, norm, type DealBadge } from './badges';
import { useBadgeSignals } from './useBadgeSignals';
import { DealPriceReporter } from '../report-problems';

// ===========================================================================
// Shared deal-card model — the merge target for a flyer deal and a store offer.
// Imported by DealsPage (the canonical home moved here so the overlay can reuse
// the exact same mappers — no fork).
// ===========================================================================
export type DealKind = 'flyer' | 'offer';

export interface DealCard {
  key: string;
  kind: DealKind;
  storeId: string;
  storeName: string;
  title: string;
  brand: string | null;
  imageUrl: string | null;
  salePrice: number | null;
  regularPrice: number | null;
  priceCurrency: string;
  /** T64.1 D8 — what `salePrice` is charged for (per item / per pound / per
   *  kilogram); null when the source supplied none (every offer card, and
   *  every deal until a source populates it). */
  priceBasis: PriceBasis | null;
  endDate: string | null;
  isFunded: boolean;
  offerId: string | null;
  flyerDealId: string | null;
  lines: FlyerDealLine[];
  systemProductId: string | null;
  systemProductName: string | null;
  packageSize: string | null;
  savingsPercent: number | null;
  savingsAmount: number | null;
}

// ---------------------------------------------------------------------------
// Formatting helpers.
// ---------------------------------------------------------------------------
export function formatPrice(value: number, currency: string): string {
  const head = `$${value.toFixed(2)}`;
  return currency && currency !== 'CAD' ? `${head} ${currency}` : head;
}

/** T64.1 D8 — human suffix for a KNOWN price basis, appended straight after
 *  `formatPrice`'s output (e.g. "$5.77" + "/lb"). Contract invariant I14: a
 *  surface renders the basis whenever one is known — `unit` is a KNOWN
 *  basis (the flyer prints "EA" on those tiles) and gets `/ea`, the direct
 *  parallel to `/lb`/`/kg`. `''` is reserved for `null` only — a source that
 *  supplied NO basis at all (every Flipp row before T64.1 D9) — never for a
 *  known basis that happens to be "each".
 *  T64.1 D10 — reads the canonical unit spelling (`lb`/`kg`/`unit`,
 *  `packages/shared/src/types/packing.ts` CANONICAL_UNITS), replacing the
 *  retired `per_pound`/`per_kilogram`/`per_item` spelling. */
export const PRICE_BASIS_SUFFIX: Record<PriceBasis, string> = {
  lb: '/lb',
  kg: '/kg',
  unit: '/ea',
};

export function priceBasisSuffix(basis: PriceBasis | null): string {
  return basis === null ? '' : PRICE_BASIS_SUFFIX[basis];
}

export function formatEndDate(validTo: string): string {
  return `ends ${validTo.slice(0, 10)}`;
}

export function packageLabel(
  value: number | null | undefined,
  unit: string | null | undefined,
): string | null {
  if (value == null) return unit ? unit : null;
  const num = Number.isInteger(value) ? String(value) : value.toFixed(2);
  return unit ? `${num} ${unit}` : num;
}

// Cross-store grouping key (D6/TD#5): the resolved SystemProduct id where
// present (none on the wire today), else a normalized title+brand fallback.
export function groupKey(card: DealCard): string {
  if (card.systemProductId) return `sp:${card.systemProductId}`;
  return `tb:${norm(card.title)}|${norm(card.brand)}`;
}

// Resolve a card's effective price (sale wins, else regular) for cross-store
// cheapest-first ordering; missing prices sort last.
export function priceOf(card: DealCard): number {
  if (card.salePrice != null) return card.salePrice;
  if (card.regularPrice != null) return card.regularPrice;
  return Number.POSITIVE_INFINITY;
}

// ---------------------------------------------------------------------------
// Card mappers.
// ---------------------------------------------------------------------------
export function flyerDealToCard(
  deal: FlyerDealsRow,
  store: FlyerDealsStoreGroup,
): DealCard {
  const sale = deal.sale_price;
  const regular = deal.regular_price ?? null;
  const savingsAmount =
    regular != null && regular > sale ? regular - sale : null;
  const savingsPercent =
    regular != null && regular > 0 && regular > sale
      ? (1 - sale / regular) * 100
      : null;
  return {
    key: `flyer-${store.storelocation_id}-${deal.id}`,
    kind: 'flyer',
    storeId: store.storelocation_id,
    storeName: store.store_name,
    title: deal.title,
    brand: deal.brand ?? null,
    imageUrl: deal.image_url ?? null,
    salePrice: sale,
    regularPrice: regular,
    priceCurrency: deal.price_currency,
    priceBasis: deal.price_basis ?? null,
    endDate: deal.valid_to,
    isFunded: true,
    offerId: deal.matched_retaileroffer_id ?? null,
    flyerDealId: deal.id,
    lines: deal.lines ?? [],
    systemProductId: null,
    systemProductName: null,
    packageSize: null,
    savingsPercent,
    savingsAmount,
  };
}

/**
 * T55.3 round-3 (owner decision) — a fit-ranked search result row into the
 * SAME `DealCard` shape the default card list renders (image, SALE badge,
 * validity, badges, add button, tappable to `DealDetailSheet`).
 * `dealsSearchRowSchema` extends `flyerDealsRowSchema` (a household-wide
 * fit-ranked read) — it carries NO store grouping the way `/api/flyerdeals`'s
 * per-store response does (the search resolver picks the best-fit deal
 * across all eligible stores, not one store's list), so `storeId`/
 * `storeName` are empty here; `DealCardRow` simply renders no store sub-line
 * for a search card. Ranking order for the search surface is the response's
 * `fit` order (server-side) under every sort; the "For you" sort (default,
 * T55.13) breaks fit ties with `dealScore`/`deriveTags` — the SAME
 * personalization signals the default card list uses, layered in
 * `DealsPage`'s `searchCardsWithTags`, not inside this mapper.
 */
export function searchRowToCard(deal: DealsSearchRow): DealCard {
  const sale = deal.sale_price;
  const regular = deal.regular_price ?? null;
  const savingsAmount =
    regular != null && regular > sale ? regular - sale : null;
  const savingsPercent =
    regular != null && regular > 0 && regular > sale
      ? (1 - sale / regular) * 100
      : null;
  return {
    key: `search-${deal.id}`,
    kind: 'flyer',
    storeId: '',
    storeName: '',
    title: deal.title,
    brand: deal.brand ?? null,
    imageUrl: deal.image_url ?? null,
    salePrice: sale,
    regularPrice: regular,
    priceCurrency: deal.price_currency,
    priceBasis: deal.price_basis ?? null,
    endDate: deal.valid_to,
    isFunded: true,
    offerId: deal.matched_retaileroffer_id ?? null,
    flyerDealId: deal.id,
    lines: deal.lines ?? [],
    systemProductId: null,
    systemProductName: null,
    packageSize: null,
    savingsPercent,
    savingsAmount,
  };
}

export function offerToCard(
  offer: RetailerOfferWithDetails,
  storeName: string,
): DealCard {
  return {
    key: `offer-${offer.storelocation_id}-${offer.id}`,
    kind: 'offer',
    storeId: offer.storelocation_id,
    storeName,
    title: offer.title,
    brand: offer.brand ?? null,
    imageUrl: offer.image_url ?? null,
    salePrice: null,
    regularPrice: offer.regular_price ?? null,
    priceCurrency: offer.price_currency,
    // An offer's wire shape carries no price basis today — never invented.
    priceBasis: null,
    endDate: null,
    isFunded: offer.has_active_deal === true,
    offerId: offer.id,
    flyerDealId: null,
    lines: [],
    systemProductId: offer.systemproduct_id ?? null,
    systemProductName: offer.system_product_name ?? null,
    packageSize: packageLabel(offer.package_size_value, offer.package_size_unit),
    savingsPercent: offer.savings_percent ?? null,
    savingsAmount: null,
  };
}

// ===========================================================================
// Tokens-only styles (moved verbatim from DealsPage).
// ===========================================================================
const TOUCH = '2.75rem';
const HAIRLINE = '0.0625rem';

const badgeToneBase: CSSProperties = {
  display: 'inline-block',
  padding: 'var(--space-1) var(--space-2)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-2xs)',
  fontWeight: 'var(--font-weight-bold)',
};
const badgePositiveStyle: CSSProperties = {
  ...badgeToneBase,
  backgroundColor: 'var(--color-accent-subtle)',
  color: 'var(--color-accent-primary)',
};
const badgeSubstituteStyle: CSSProperties = {
  ...badgeToneBase,
  backgroundColor: 'var(--color-bg-raised)',
  color: 'var(--color-text-muted)',
};
const badgeNegativeStyle: CSSProperties = {
  ...badgeToneBase,
  backgroundColor: 'var(--color-status-danger-subtle, var(--color-bg-raised))',
  color: 'var(--color-status-danger, var(--color-text-muted))',
};
export function badgeStyleFor(tone: DealBadge['tone']): CSSProperties {
  if (tone === 'positive') return badgePositiveStyle;
  if (tone === 'substitute') return badgeSubstituteStyle;
  return badgeNegativeStyle;
}

const sheetBodyStyle: CSSProperties = {
  padding: `var(--space-3) var(--space-4) var(--space-6)`,
};
const sheetHeroStyle: CSSProperties = {
  width: '100%',
  display: 'grid',
  placeItems: 'center',
  marginBottom: 'var(--space-3)',
};
const sheetHeroImageStyle: CSSProperties = {
  width: '100%',
  maxWidth: 'calc(var(--space-12) * 8)',
  height: 'auto',
  borderRadius: 'var(--radius-md)',
  objectFit: 'contain',
};
const sheetHeroFallbackStyle: CSSProperties = {
  width: '100%',
  maxWidth: 'calc(var(--space-12) * 8)',
  height: 'calc(var(--space-12) * 2 + var(--space-6))',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-xl)',
};
const sheetTitleStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--space-2)',
  // T47 Phase 4 — deal sheet title (the product name heading) → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-md)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
  margin: 0,
};
const sheetMetaStyle: CSSProperties = {
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};
const sheetTagWrapStyle: CSSProperties = {
  marginTop: 'var(--space-2)',
};
const sheetPriceStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-3)',
};
const sheetNowSaleStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-accent-tertiary)',
};
const sheetNowRegularStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
};
// T64.1 D8 — the unit the price is charged for, set inline right after the
// amount ("$4.99 /lb") the way the source flyer prints it. Lighter and smaller
// so the number stays the focal point; never its own row.
const sheetBasisStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-regular)',
  color: 'var(--color-text-muted)',
};
const sheetWasStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
  textDecorationLine: 'line-through',
};
const sheetSaveStyle: CSSProperties = {
  display: 'inline-block',
  padding: 'var(--space-1) var(--space-2)',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-status-success-subtle)',
  color: 'var(--color-status-success)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
};
const sheetSecStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xs)',
  fontWeight: 'var(--font-weight-bold)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-6)',
  marginBottom: 'var(--space-2)',
};
const sheetStoreRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) 0',
  fontSize: 'var(--font-size-xs)',
};
const sheetStoreRowDividedStyle: CSSProperties = {
  ...sheetStoreRowStyle,
  borderTopWidth: HAIRLINE,
  borderTopStyle: 'solid',
  borderTopColor: 'var(--color-border-subtle)',
};
const sheetStoreLeftStyle: CSSProperties = {
  minWidth: 0,
  color: 'var(--color-text-primary)',
};
const sheetStoreSubStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xs)',
  color: 'var(--color-text-muted)',
};
const sheetStorePriceStyle: CSSProperties = {
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: 'var(--space-1)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
};
const sheetStoreWasStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xs)',
  color: 'var(--color-text-muted)',
  textDecorationLine: 'line-through',
};
const sheetActionsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  marginTop: 'var(--space-6)',
};
const sheetAddStyle: CSSProperties = {
  width: '100%',
  minHeight: TOUCH,
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  backgroundColor: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  fontSize: 'var(--font-size-base15)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
};
const sheetAddDisabledStyle: CSSProperties = {
  ...sheetAddStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-muted)',
  cursor: 'not-allowed',
  opacity: 0.7,
};
const sheetOnListStyle: CSSProperties = {
  textAlign: 'center',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-status-success)',
};
const sheetRemoveStyle: CSSProperties = {
  width: '100%',
  minHeight: TOUCH,
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-base15)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
};
export const saleBadgeStyle: CSSProperties = {
  display: 'inline-block',
  marginLeft: 'var(--space-2)',
  padding: 'var(--space-1) var(--space-2)',
  borderRadius: 'var(--radius-sm)',
  backgroundColor: 'var(--color-status-danger-subtle)',
  color: 'var(--color-accent-tertiary)',
  fontSize: 'var(--font-size-2xs)',
  fontWeight: 'var(--font-weight-bold)',
  verticalAlign: 'middle',
};
const overlayLoadingStyle: CSSProperties = {
  padding: 'var(--space-6)',
  textAlign: 'center',
  color: 'var(--color-text-muted)',
};

// ===========================================================================
// DealDetailSheet — the one deal pop-up (bottom-sheet Modal variant). Moved here
// from DealsPage so both the Deals page and the list can mount the same sheet.
// ===========================================================================
export function DealDetailSheet({
  card,
  badges,
  allCards,
  selectedStoreIds,
  onList,
  onClose,
  onAdd,
  onRemove,
}: {
  card: DealCard;
  badges: DealBadge[];
  allCards: DealCard[];
  selectedStoreIds: string[];
  onList: boolean;
  onClose: () => void;
  onAdd: () => void;
  onRemove: () => void;
}): JSX.Element {
  const onSale = card.salePrice != null;
  // Contract invariant I14 — render the basis whenever one is KNOWN, sale or
  // regular. `''` only when the source supplied none.
  const basisSuffix = priceBasisSuffix(card.priceBasis);
  const noAnchor = card.offerId == null && card.lines.length === 0;
  const now = onSale ? (card.salePrice as number) : card.regularPrice;
  const validity = onSale
    ? `Flyer deal${card.endDate ? ` · ${formatEndDate(card.endDate)}` : ''}`
    : 'Regular price';

  const saveLabel = (() => {
    if (!onSale) return null;
    const amount = card.savingsAmount;
    const pct = card.savingsPercent;
    if (amount != null && pct != null) {
      return `Save $${amount.toFixed(2)} (${Math.round(pct)}%)`;
    }
    if (amount != null) return `Save $${amount.toFixed(2)}`;
    if (pct != null) return `Save ${Math.round(pct)}%`;
    return null;
  })();

  const selectedSet = useMemo(
    () => new Set(selectedStoreIds),
    [selectedStoreIds],
  );
  const others = useMemo(() => {
    const key = groupKey(card);
    return allCards
      .filter(
        (c) =>
          c.key !== card.key &&
          groupKey(c) === key &&
          selectedSet.has(c.storeId),
      )
      .sort((a, b) => priceOf(a) - priceOf(b));
  }, [allCards, card, selectedSet]);

  return (
    <Modal
      open
      onClose={onClose}
      variant="sheet"
      ariaLabel={card.title}
      hideCloseButton={false}
    >
      <div style={sheetBodyStyle} data-testid="deal-sheet">
        <div style={sheetHeroStyle} aria-hidden="true">
          {card.imageUrl ? (
            <img src={card.imageUrl} alt="" style={sheetHeroImageStyle} />
          ) : (
            <div style={sheetHeroFallbackStyle}>🏷️</div>
          )}
        </div>

        <h3 style={sheetTitleStyle}>
          {card.title}
          {onSale && <span style={saleBadgeStyle}>SALE</span>}
        </h3>

        {/* Package size joins the meta line when the source carries one (offer
            cards). Weighed produce has no package — its unit rides the price
            line as `/lb`, so no empty size row is rendered for it. */}
        <div style={sheetMetaStyle}>
          {[card.brand, card.storeName, card.packageSize, validity]
            .filter(Boolean)
            .join(' · ')}
        </div>

        {badges.length > 0 && (
          <div style={sheetTagWrapStyle}>
            {badges.map((b) => (
              <span
                key={b.kind}
                style={badgeStyleFor(b.tone)}
                data-testid={`deal-sheet-tag-${b.testidSuffix}`}
              >
                {b.label}
              </span>
            ))}
          </div>
        )}

        <div style={sheetPriceStyle}>
          {now != null && (
            <span
              style={onSale ? sheetNowSaleStyle : sheetNowRegularStyle}
              data-testid="deal-sheet-now"
            >
              {formatPrice(now, card.priceCurrency)}
              {basisSuffix && (
                <span style={sheetBasisStyle} data-testid="deal-sheet-basis">
                  {' '}
                  {basisSuffix}
                </span>
              )}
            </span>
          )}
          {onSale &&
            card.regularPrice != null &&
            card.regularPrice > (card.salePrice as number) && (
              <span style={sheetWasStyle}>
                {formatPrice(card.regularPrice, card.priceCurrency)}
              </span>
            )}
          {saveLabel && (
            <span style={sheetSaveStyle} data-testid="deal-sheet-save">
              {saveLabel}
            </span>
          )}
        </div>

        {others.length > 0 && (
          <div data-testid="deal-sheet-compare">
            <div style={sheetSecStyle}>Also at your stores · cheapest first</div>
            {others.map((o, i) => {
              const oSale = o.salePrice != null;
              const oNow = oSale ? (o.salePrice as number) : o.regularPrice;
              return (
                <div
                  key={o.key}
                  style={i === 0 ? sheetStoreRowStyle : sheetStoreRowDividedStyle}
                  data-testid={`deal-sheet-compare-${o.key}`}
                >
                  <div style={sheetStoreLeftStyle}>
                    {o.storeName}
                    <span style={sheetStoreSubStyle}> · {o.title}</span>
                  </div>
                  <div style={sheetStorePriceStyle}>
                    {oNow != null && formatPrice(oNow, o.priceCurrency)}
                    {oSale &&
                      o.regularPrice != null &&
                      o.regularPrice > (o.salePrice as number) && (
                        <span style={sheetStoreWasStyle}>
                          {formatPrice(o.regularPrice, o.priceCurrency)}
                        </span>
                      )}
                    {oSale && <span style={saleBadgeStyle}>SALE</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={sheetActionsStyle}>
          {noAnchor ? (
            <button
              type="button"
              style={sheetAddDisabledStyle}
              disabled
              title="No product to anchor this to yet."
              data-testid="deal-sheet-add"
            >
              + Add to list
            </button>
          ) : onList ? (
            <>
              <div style={sheetOnListStyle} data-testid="deal-sheet-onlist">
                ✓ On your list · {card.storeName}
                {now != null
                  ? ` · ${formatPrice(now, card.priceCurrency)}${basisSuffix}`
                  : ''}
              </div>
              <button
                type="button"
                style={sheetRemoveStyle}
                onClick={onRemove}
                data-testid="deal-sheet-remove"
              >
                Remove from list
              </button>
            </>
          ) : (
            <button
              type="button"
              style={sheetAddStyle}
              onClick={onAdd}
              data-testid="deal-sheet-add"
            >
              + Add to list
            </button>
          )}
        </div>

        {/* Proto: `.ds-reportprice` — quiet pull link below the add/remove actions. */}
        {card.flyerDealId != null && (
          <DealPriceReporter
            offerId={card.offerId}
            dealLabel={card.title}
          />
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// StoreLocationOffersLoader — one per selected store. Hooks can't loop, so each store's
// `useRetailerOffers` lives in its own child that reports its offers up.
// (Mirrors the loader DealsPage uses — kept self-contained for the overlay so
// the in-place sheet builds the SAME full card set without forking the page.)
// ---------------------------------------------------------------------------
interface OverlayOffersReport {
  storeId: string;
  offers: RetailerOfferWithDetails[];
}

function OverlayStoreLocationOffersLoader({
  storeId,
  onReport,
}: {
  storeId: string;
  onReport: (report: OverlayOffersReport) => void;
}): null {
  const query = useRetailerOffers(storeId);
  const offers = query.data?.offers;
  useEffect(() => {
    onReport({ storeId, offers: offers ?? [] });
  }, [storeId, offers, onReport]);
  return null;
}

// ===========================================================================
// DealOverlay — the list's in-place entry point. Mounts the same data the Deals
// page mounts, builds the full card set, finds the card for `flyerDealId`, and
// renders `DealDetailSheet`. Closes via `onClose` (no route change).
// ===========================================================================
export function DealOverlay({
  flyerDealId,
  onClose,
}: {
  flyerDealId: string;
  onClose: () => void;
}): JSX.Element | null {
  const selectedStoresQuery = useSelectedStores();
  const storesQuery = useStores();
  const activeListQuery = useActiveShoppingList();
  const productsQuery = useHouseholdProducts();
  const addToList = useAddToActiveList();
  const deleteItem = useDeleteShoppingListItem();
  const badgeSignals = useBadgeSignals();

  const selectedStoreIds = useMemo(
    () => selectedStoresQuery.data?.storeIds ?? [],
    [selectedStoresQuery.data],
  );
  const storeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const store of storesQuery.data?.stores ?? []) {
      map.set(store.id, store.name);
    }
    return map;
  }, [storesQuery.data?.stores]);

  const [offerReports, setOfferReports] = useState<
    Record<string, OverlayOffersReport>
  >({});
  const handleReport = useCallback((report: OverlayOffersReport) => {
    setOfferReports((prev) => {
      const existing = prev[report.storeId];
      if (existing && existing.offers === report.offers) return prev;
      return { ...prev, [report.storeId]: report };
    });
  }, []);

  const flyerQuery = useFlyerDeals(false, false);
  const flyerStores: FlyerDealsStoreGroup[] = useMemo(
    () => flyerQuery.data?.stores ?? [],
    [flyerQuery.data],
  );

  // The full card set — the SAME merge DealsPage performs (flyer deals across
  // all selected stores + each store's offers). Loading the full set keeps the
  // cross-store comparison block alive in the in-place sheet.
  const allCards = useMemo<DealCard[]>(() => {
    const out: DealCard[] = [];
    for (const store of flyerStores) {
      for (const deal of store.deals) {
        out.push(flyerDealToCard(deal, store));
      }
    }
    for (const storeId of selectedStoreIds) {
      const report = offerReports[storeId];
      if (!report) continue;
      const storeName = storeNameById.get(storeId) ?? storeId;
      for (const offer of report.offers) {
        out.push(offerToCard(offer, storeName));
      }
    }
    return out;
  }, [flyerStores, selectedStoreIds, offerReports, storeNameById]);

  const card = useMemo(
    () => allCards.find((c) => c.flyerDealId === flyerDealId) ?? null,
    [allCards, flyerDealId],
  );

  // ---- On-list membership + badges (same logic as DealsPage) ----------------
  const activeListId = activeListQuery.data?.list?.id ?? null;
  const itemsByOfferId = useMemo(() => {
    const map = new Map<string, ShoppingListItemWithDetails>();
    for (const item of activeListQuery.data?.items ?? []) {
      if (item.status !== 'pending') continue;
      if (item.selected_retaileroffer_id) {
        map.set(item.selected_retaileroffer_id, item);
      }
    }
    return map;
  }, [activeListQuery.data]);
  const itemsByDealId = useMemo(() => {
    const map = new Map<string, ShoppingListItemWithDetails>();
    for (const item of activeListQuery.data?.items ?? []) {
      if (item.status !== 'pending') continue;
      if (item.flyerdeal_id) {
        map.set(item.flyerdeal_id, item);
      }
    }
    return map;
  }, [activeListQuery.data]);
  const onListSystemProductIds = useMemo(() => {
    const productLinks = new Map<string, string[]>();
    for (const product of productsQuery.data?.products ?? []) {
      productLinks.set(product.id, product.linked_systemproduct_ids ?? []);
    }
    const set = new Set<string>();
    for (const item of activeListQuery.data?.items ?? []) {
      if (item.status !== 'pending') continue;
      const ids = productLinks.get(item.householdproduct_id);
      if (ids) for (const id of ids) set.add(id);
    }
    return set;
  }, [activeListQuery.data, productsQuery.data]);

  const cardOnList = useCallback(
    (c: DealCard): boolean =>
      (!!c.offerId && itemsByOfferId.has(c.offerId)) ||
      (!c.offerId && !!c.flyerDealId && itemsByDealId.has(c.flyerDealId)),
    [itemsByOfferId, itemsByDealId],
  );
  const cardOnListBadge = useCallback(
    (c: DealCard): boolean => {
      if (cardOnList(c)) return true;
      if (onListSystemProductIds.size === 0) return false;
      return c.systemProductId != null && onListSystemProductIds.has(c.systemProductId);
    },
    [cardOnList, onListSystemProductIds],
  );
  const cardBadges = useCallback(
    (c: DealCard): DealBadge[] =>
      deriveBadges(c, { onList: cardOnListBadge(c), ...badgeSignals }),
    [cardOnListBadge, badgeSignals],
  );

  // ---- Add / remove (the offer-less single-line + offer paths; combo /
  // confirm-create stay on the Deals page where the prompts live) ------------
  const handleAdd = useCallback(
    (c: DealCard) => {
      if (c.offerId) {
        const offerId = c.offerId;
        void addToList
          .mutateAsync({ selectedRetailerOfferId: offerId, quantity: 1 })
          .catch(() => {});
        return;
      }
      if (!c.flyerDealId || c.lines.length === 0) return;
      // A combo (several lines) needs the picker, which lives on the Deals page;
      // a single-line deal adds straight away here.
      if (c.lines.length > 1) return;
      const request: AddShoppingListItemRequest = {
        flyerDealId: c.flyerDealId,
        quantity: 1,
      };
      void addToList.mutateAsync(request).catch(() => {});
    },
    [addToList],
  );
  const handleRemove = useCallback(
    (c: DealCard) => {
      if (!activeListId) return;
      const item = c.offerId
        ? itemsByOfferId.get(c.offerId)
        : c.flyerDealId
          ? itemsByDealId.get(c.flyerDealId)
          : undefined;
      if (!item) return;
      deleteItem.mutate({ listId: activeListId, itemId: item.id });
    },
    [deleteItem, activeListId, itemsByOfferId, itemsByDealId],
  );

  // The per-store offer loaders ALWAYS render (even before the card is found) so
  // the full card set — including offers for the cross-store comparison — loads.
  const loaders = selectedStoreIds.map((storeId) => (
    <OverlayStoreLocationOffersLoader
      key={storeId}
      storeId={storeId}
      onReport={handleReport}
    />
  ));

  // While the flyer set is still loading, show a lightweight sheet shell so the
  // overlay opens immediately (no jump to a blank list).
  if (flyerQuery.isLoading && allCards.length === 0) {
    return (
      <>
        {loaders}
        <Modal open onClose={onClose} variant="sheet" ariaLabel="Loading deal">
          <div style={overlayLoadingStyle} data-testid="deal-overlay-loading">
            Loading deal…
          </div>
        </Modal>
      </>
    );
  }

  // The deal purged (FK nulled) or isn't visible at any selected store: nothing
  // to show. Close so the row falls back to its product summary.
  if (!card) {
    return (
      <>
        {loaders}
        <Modal open onClose={onClose} variant="sheet" ariaLabel="Deal unavailable">
          <div style={overlayLoadingStyle} data-testid="deal-overlay-missing">
            This deal is no longer available.
          </div>
        </Modal>
      </>
    );
  }

  return (
    <>
      {loaders}
      <DealDetailSheet
        card={card}
        badges={cardBadges(card)}
        allCards={allCards}
        selectedStoreIds={selectedStoreIds}
        onList={cardOnList(card)}
        onClose={onClose}
        onAdd={() => handleAdd(card)}
        onRemove={() => handleRemove(card)}
      />
    </>
  );
}
